import React, { memo } from "react";
import { Grid, GridItem, Level, LevelItem, Tooltip, Label, LabelColor, Content, ClipboardCopy, Panel, PanelHeader, PanelMain, PanelMainBody, Divider } from "@patternfly/react-core";
import Moment from "moment";
import { formatPods } from "../utils/formatPods";
import PodsTable from "./PodsTable";
import { formatBytes } from "shared/utils/formatBytes";

const DeploymentsSection = memo(({
  userProfile,
  deployments,
  pods = [],
  services = [],
  servicesLoading = false,
  servicesError = null,
}) => {
  const getDeploymentPods = (deploymentName) => {
    return pods.filter((pod) =>
      pod.name.startsWith(`${deploymentName}-`)
    );
  };
  const getDeploymentServices = (deploymentName) => {
    const deploymentPods = getDeploymentPods(deploymentName);

    return services.filter((service) => {
      const selector = service.selector || {};
      const selectorEntries = Object.entries(selector);

      if (selectorEntries.length === 0) {
        return false;
      }

      return deploymentPods.some((pod) => {
        const labels = pod.labels || {};

        return selectorEntries.every(
          ([key, value]) => labels[key] === value
        );
      });
    });
  };
  if (!deployments?.length) return null;

  return (
    <Grid hasGutter>
      {deployments.map((deploy) => (
        <GridItem key={deploy?.id || deploy.name}>
          <Panel variant="bordered">
            {/* <div key={deploy?.id || deploy.name}> */}
            <PanelHeader>
              <Level>
                <LevelItem>
                  <Tooltip content="Deployment">
                    <Label color={LabelColor.teal} isCompact>D</Label>
                  </Tooltip>{" "}
                  <b>{deploy.name}-deployment</b>{" "}
                  {formatPods(deploy.replicas, deploy.available) && (
                    <span>{formatPods(deploy.replicas, deploy.available)}</span>
                  )}
                </LevelItem>

                <LevelItem>
                  <b>Utworzono:</b>{" "}
                  {Moment(deploy.creationTimestamp).format("DD.MM.YYYY H:mm a")}
                </LevelItem>
              </Level>
            </PanelHeader>
            <Divider />
            <PanelMain>
              <PanelMainBody>
                <Level>
                  <LevelItem>
                    <Tooltip content="Route">
                      <Label isCompact>R</Label>
                      </Tooltip>{' '}
                      <b>Route</b>
                  </LevelItem>
                </Level>
                <table className="table">
                  <tbody>
                    <tr>
                      <th>Alias</th>
                      <th>Original url</th>
                    </tr>
                    {deploy?.routes?.map((route) => (
                      <tr key={route?.id || route.name}>
                        <td>
                          <Content
                            component="a"
                            target="_blank"
                            href={`https://${deploy.name}-${userProfile?.username}.zspw.pl`}
                          >
                            {deploy.name}-{userProfile?.username}.zspw.pl
                          </Content>
                        </td>
                        <td className="small">
                          <ClipboardCopy
                            variant="inline-compact"
                            hoverTip="Kopiuj"
                            clickTip="Skopiowano"
                          >
                            {/* {route.host} */}
                            <Content
                              component="a"
                              target="_blank"
                              href={`https://${route.host}`}
                            >
                              {route.host}
                            </Content>
                          </ClipboardCopy>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {userProfile?.is_superuser && (
                  <>
                    {/* Services */}
                    <Level style={{ marginTop: 5 }}>
                      <LevelItem>
                        <Tooltip content="Services">
                          <Label isCompact>S</Label>
                        </Tooltip>{" "}
                        Services
                      </LevelItem>
                    </Level>

                    <table className="table">
                      <tbody>
                        <tr>
                          <th>Name</th>
                          <th>Type</th>
                          {/* <th>ClusterIP</th> */}
                          <th>Port</th>
                          <th>TargetPort</th>
                          <th>NodePort</th>
                          <th>Protocol</th>
                        </tr>

                        {getDeploymentServices(deploy.name).flatMap(
                          (service) =>
                            (service.ports?.length
                              ? service.ports
                              : [null]
                            ).map((port, index) => (
                              <tr
                                key={`${service.name}-${index}`}
                              >
                                <td>{service.name}</td>

                                <td>
                                    {service.type}
                                </td>

                                {/* <td>
                                  {service.cluster_ip || "-"}
                                </td> */}

                                <td>{port?.port ?? "-"}</td>

                                <td>
                                  {port?.target_port ?? "-"}
                                </td>

                                <td>
                                  {port?.node_port != null ? (
                                    <ClipboardCopy
                                      variant="inline-compact"
                                      hoverTip="Kopiuj"
                                      clickTip="Skopiowano"
                                    >
                                      {String(port.node_port)}
                                    </ClipboardCopy>
                                  ) : (
                                    "-"
                                  )}
                                </td>

                                <td>
                                  {port?.protocol || "-"}
                                </td>
                              </tr>
                            ))
                        )}

                        {servicesLoading && (
                          <tr>
                            <td colSpan={7}>
                              Ładowanie usług...
                            </td>
                          </tr>
                        )}

                        {!servicesLoading && servicesError && (
                          <tr>
                            <td colSpan={7} role="alert">
                              Błąd pobierania usług:{" "}
                              {servicesError.message}
                            </td>
                          </tr>
                        )}

                        {!servicesLoading &&
                          !servicesError &&
                          getDeploymentServices(deploy.name)
                            .length === 0 && (
                            <tr>
                              <td colSpan={7}>
                                Brak usług dla tego Deploymentu.
                              </td>
                            </tr>
                          )}
                      </tbody>
                    </table>

                    {/* Containers */}
                  <Level style={{ marginTop: 5 }}>
                      <LevelItem>
                      <Tooltip content="Containers">
                          <Label isCompact>C</Label>
                      </Tooltip>{" "}
                      Container
                      </LevelItem>
                  </Level>
                  <table className="table">
                    <tbody>
                      <tr>
                        <th>Name</th>
                        <th>Image</th>
                        <th>Image Pull Policy</th>
                      </tr>

                      {deploy?.containers?.map((c) => (
                        <tr key={c.name}>
                          <td>{c.name}</td>
                          <td>{c.image}</td>
                          <td>{c.image_pull_policy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* start Pods */}
                  <Level style={{ marginTop: 5 }}>
                    <LevelItem>
                      <Tooltip content="Pods">
                        <Label isCompact>P</Label>
                      </Tooltip>{" "}
                      Pods
                    </LevelItem>
                  </Level>

                  <PodsTable
                    pods={getDeploymentPods(deploy.name)}
                    loading={false}
                    error={null}
                    onRefetch={() => {}}
                    formatBytes={formatBytes}
                  />
                  {/* End Pods */}
                  {deploy?.volumes.length > 0 &&
                    <>
                    <Level style={{ marginTop: 5 }}>
                        <LevelItem>
                        <Tooltip content="Volumes">
                            <Label isCompact>V</Label>
                        </Tooltip>{" "}
                        Volumes
                        </LevelItem>
                    </Level>
                    <table className="table">
                      <tbody>
                        <tr>
                          <th>Name</th>
                          <th>Claim</th>
                        </tr>
                        {deploy?.volumes
                          ?.filter((v) => !v.emptyDir)
                          .map((v) => (
                            <tr key={v.name}>
                              <td>{v.name}</td>
                              <td>{v?.persistentVolumeClaim?.claimName}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    </>
                  }
                </>)}
              </PanelMainBody>
            </PanelMain>
          </Panel>
        </GridItem>
      ))}
    </Grid>
  );
});

export default DeploymentsSection;